import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router, RouteConfigLoadStart } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/zip';
import {
  CrawfordPageBannerService, CrawfordPageTitleService, CrawfordSnackbarService, CrawfordSpinnerService,
  CrawfordTabComponent, ITabComponent, TabItem, TabItemHeader, IPageTitleAction
} from 'crawford-shell';
import { UserDetails } from '../../user/user-details.model';
import { UserService } from '../../user/user.service';
import { ClaimService } from '../claim.service';
import { CoreClaimDetail } from '../model/core-claim-detail.model';
import { Subscription } from 'rxjs/Subscription';


@Component({
  selector: 'app-claim-detail',
  templateUrl: './claim-detail.component.html',
  styleUrls: ['./claim-detail.component.scss']
})
export class ClaimDetailComponent implements OnInit {

  @ViewChild('tabCollection') public tabCollection: CrawfordTabComponent;
  @ViewChild('claimAdjusterFeesTab') public claimAdjusterFeesTab: ITabComponent;
  @ViewChild('claimAdjusterInfoTab') public claimAdjusterInfoTab: ITabComponent;
  @ViewChild('claimDatesTab') public claimDatesTab: ITabComponent;
  @ViewChild('claimPartiesTab') public claimPartiesTab: ITabComponent;
  @ViewChild('claimAssociatedClaimsTab') public claimAssociatedClaimsTab: ITabComponent;
  @ViewChild('claimFeesDisbursementsTab') public claimFeesDisbursementsTab: ITabComponent;
  @ViewChild('claimReservesPaymentsTab') public claimReservesPaymentsTab: ITabComponent;
  @ViewChild('claimEventListTab') public claimEventListTab: ITabComponent;
  @ViewChild('claimDocumentListTab') public claimDocumentListTab: ITabComponent;
  @ViewChild('claimPresentPositionTab') public claimPresentPositionTab: ITabComponent;
  @ViewChild('claimMediaTab') public claimMediaTab: ITabComponent;

  public claimDetailsForm: FormGroup;
  public claimOpen: boolean;
  public claimTabs: TabItem[] = [];
  public crawfordRef: string;
  public source: string;
  public tabComponentsSet: boolean;
  public userType: string;
  public claimLoaded = false;
  public showPresentPosition = false;
  public mediaViewerUrl: string; // This will be loaded from API -
  private showAdjusterFeesTab = false;
  private getCoreClaimDetailsObservable: Subscription;
  private claimTabCounter = 0;
  private tabLocations: any = {};

  private userDetails: UserDetails;

  constructor(private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private claimService: ClaimService,
    private router: Router,
    public snackbarService: CrawfordSnackbarService,
    private pageTitleService: CrawfordPageTitleService,
    private bannerService: CrawfordPageBannerService,
    private spinnerService: CrawfordSpinnerService,
    private userService: UserService
  ) {
    this.spinnerService.startPageSpinner();
    this.router.events.subscribe((x: RouteConfigLoadStart) => {
      if (this.getCoreClaimDetailsObservable && !this.getCoreClaimDetailsObservable.closed) {
        this.getCoreClaimDetailsObservable.unsubscribe();
      }
    });
  }

  public ngOnInit(): void {
    this.userDetails = this.userService.userDetails;
    this.tabComponentsSet = false;
    this.claimTabs = [
      new TabItem(new TabItemHeader('Present Position'), '#claimPresentPosition'),
      new TabItem(new TabItemHeader('Claim Dates'), '#claimDates'),
      new TabItem(new TabItemHeader('Claim Parties'), '#claimParties'),
      new TabItem(new TabItemHeader('Linked Files'), '#linkedFiles'),
      new TabItem(new TabItemHeader('Reserves and Payments'), '#reservesAndPayments'),
      new TabItem(new TabItemHeader('Fees and Disbursements'), '#feesAndDisbursements'),
      new TabItem(new TabItemHeader('Adjuster Fees'), '#claimAdjusterFees'),
      new TabItem(new TabItemHeader('Events'), '#events'),
      new TabItem(new TabItemHeader('Documents'), '#documents'),
      new TabItem(new TabItemHeader('Media'), '#claimMedia'),
      new TabItem(new TabItemHeader('Crawford Contact'), '#claimAdjusterInfo')
    ];

    const userAccessErrorMessage = this.userDetails.userType === 'Internal' ?
      'You do not have access to this extra secure claim' : 'You do not have access to this claim';

    this.activatedRoute.params.subscribe(params => {
      if (!params.id) {
        this.spinnerService.stopPageSpinner();
        this.router.navigate(['/claim/search']);
        this.snackbarService.displayErrorMessage('No Crawford Reference was provided');
        return;
      }

      this.source = params.source ? params.source : 'Current';

      this.crawfordRef = params.id;

      this.claimService.checkUserHasAccess(this.crawfordRef).subscribe(access => {
          if (access) {
            this.createDetailsForm();
            this.claimDetailsForm.disable();
            this.getCoreClaimDetails();
          } else if (!access) {
            this.spinnerService.stopPageSpinner();
            this.router.navigate(['/claim/search']);
            this.snackbarService.displayErrorMessage(userAccessErrorMessage);
          }
      });
    });
  }

  private setMediaViewerUrl(url: string): void {
    this.mediaViewerUrl = url;
  }

  private setInactivityFlag(isInactive: boolean): void {
    if (isInactive) {
      const inactiveAction: IPageTitleAction = {
        icon: 'fa fa-fw fa-exclamation-triangle',
        title: 'Inactive',
        alert: true
      };

      this.pageTitleService.addAction(inactiveAction);
    }
  }

  private setFeesTabAccessPermission(hasAccess: boolean) {
    this.showAdjusterFeesTab = hasAccess;
  }

  getCoreClaimDetails(): void {
    this.getCoreClaimDetailsObservable = Observable.zip(
      this.claimService.getClaimById(this.crawfordRef, this.source),
      this.claimService.getClaimComplaintsGrumblesStatus(this.crawfordRef),
      this.claimService.checkUserFeesAccess(this.crawfordRef),
      this.claimService.getClaimInactivityStatus(this.crawfordRef),
      this.claimService.getClaimShowPresentPosition(this.crawfordRef),
      this.claimService.getMediaViewerUrl(this.crawfordRef)
    ).subscribe(
      (claimData) => {
        if (claimData) {
          this.loadClaimDetails(claimData[0]);
          this.loadGrumblesAndComplaints(claimData[1]);
          this.setFeesTabAccessPermission(claimData[2]);
          this.setInactivityFlag(claimData[3]);
          this.showHidePresentPosition(claimData[4]);
          this.setMediaViewerUrl(claimData[5]);
        } else {
          this.router.navigate(['/claim/search']);
          this.snackbarService.displayErrorMessage('No claim was found for Claim: ' + this.crawfordRef);
        }

        this.spinnerService.stopPageSpinner();
      },
      error => {
        this.router.navigate(['/claim/search']);
      }
    );
  }

  loadClaimDetails(claimDetail: CoreClaimDetail): void {
    if (claimDetail) {
      const pageTitle = `${claimDetail.crawfordRef}${(claimDetail.policyholderName ? ': ' + claimDetail.policyholderName : '')}`;
      this.pageTitleService.setTitle(pageTitle);
      this.setFormValues(claimDetail);
      this.crawfordRef = claimDetail.crawfordRef;
      this.claimLoaded = true;
    } else {
      this.router.navigate(['/claim/search']);
      this.snackbarService.displayErrorMessage('No claim was found for Claim: ' + this.crawfordRef);
    }
  }

  loadGrumblesAndComplaints(status): void {
    if (status) {
      this.bannerService.displayErrorBanner(status);
    } else {
      this.bannerService.hideBanner();
    }
  }

  private createDetailsForm(): any {
    this.claimDetailsForm = this.formBuilder.group({
      crawfordRef: new FormControl(),
      insurer: new FormControl(),
      broker: new FormControl(),
      policyNumber: new FormControl(),
      policyholderName: new FormControl(),
      policyholderPostcode: new FormControl(),
      lossStatus: new FormControl(),
      lossPostcode: new FormControl(),
      claimNumber: new FormControl(),
      claimType: new FormControl(),
      delegatedAuthority: new FormControl(),
      lossDate: new FormControl()
    });
  }

  public onSelected(event: any) {
    this.setTabs();

    if (this.userDetails.userType !== 'Internal') {
      this.tabCollection.hideTab(this.tabLocations.claimAssociatedClaimsTab);
    }

    if (this.userDetails.userType !== 'External') {
      this.tabCollection.hideTab(this.tabLocations.claimMediaTab);
    }

    if (this.showAdjusterFeesTab) {
      this.tabCollection.showTab(this.tabLocations.claimFeesDisbursementsTab);
      this.tabCollection.showTab(this.tabLocations.claimAdjusterFeesTab);
    } else {
      this.tabCollection.hideTab(this.tabLocations.claimFeesDisbursementsTab);
      this.tabCollection.hideTab(this.tabLocations.claimAdjusterFeesTab);
    }
  }

  private setFormValues(claimDetail: any) {

    claimDetail.lossDate = claimDetail.lossDate ? new Date(claimDetail.lossDate).toLocaleDateString() : null;
    this.claimDetailsForm.reset(claimDetail);

    this.claimOpen = claimDetail.lossStatus === 'Outstanding';

    const hasComplaint = false; // determine if the claim has a complaint
    const hasGrumble = false; // determine if the claim has a gumble

    if (hasComplaint) {
      this.bannerService.displayErrorBanner('Complaint');
    }
  }

  private showHidePresentPosition(showPresentPosition: boolean) {
    this.showPresentPosition = showPresentPosition;
  }

  setTabs(): any {
    if (!this.tabComponentsSet) {
      this.addToClaimTabs('claimPresentPositionTab', this.claimPresentPositionTab);
      this.addToClaimTabs('claimDatesTab', this.claimDatesTab);
      this.addToClaimTabs('claimPartiesTab', this.claimPartiesTab);
      this.addToClaimTabs('claimAssociatedClaimsTab', this.claimAssociatedClaimsTab);
      this.addToClaimTabs('claimReservesPaymentsTab', this.claimReservesPaymentsTab);
      this.addToClaimTabs('claimFeesDisbursementsTab', this.claimFeesDisbursementsTab);
      this.addToClaimTabs('claimAdjusterFeesTab', this.claimAdjusterFeesTab);
      this.addToClaimTabs('claimEventListTab', this.claimEventListTab);
      this.addToClaimTabs('claimDocumentListTab', this.claimDocumentListTab);
      this.addToClaimTabs('claimMediaTab', this.claimMediaTab);
      this.addToClaimTabs('claimAdjusterInfoTab', this.claimAdjusterInfoTab);
      this.tabComponentsSet = true;

      if (this.showPresentPosition) {
        this.tabCollection.showTab(this.tabLocations.claimPresentPositionTab);
      } else {
        this.tabCollection.hideTab(this.tabLocations.claimPresentPositionTab);
      }
    }
  }

  private addToClaimTabs(tabName: string, tab: ITabComponent) {
    this.tabLocations[tabName] = this.claimTabCounter;
    this.claimTabs[this.claimTabCounter].component = tab;
    this.claimTabCounter++;
  }
}



// WEBPACK FOOTER //
// D:/a/1/s/UI/src/app/claim/claim-detail/claim-detail.component.ts